import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import RL from './route-literals';
import { TcpTypes } from '../slices/terms-conditions-policies';
import ProtectedRoute from '../components/auth/protected-route';
import AntiProtectedRoute from '../components/auth/anti-protected-route';
import { SeriesTabsIds, SeriesDetailsTabsIds } from './for-stamps';
import { PdfEditorTabs } from './for-pdf-editor';
import { DocumentTypes } from './document-types';
import { WorkflowsTabsIds } from './for-workflows';
import { PurchaseHistoryTabsIds } from './for-purchase-history';
import { FeatureKeys } from './auth-rules';
import { ConsumptionHistoryTabsIds } from './for-consumption-history';
import { EbgNotificationsTabsIds } from './for-ebg-notifications';
import { InvoicesTabsIds } from './for-invoices';
import { ForDocumentDetails } from './for-document-details';
import notification from '../components/views/notification';
import { DepartmentNeslConfigsTabsIds } from './for-department-nesl-configs';
import { FeatureConfigIds } from './feature-config-rules';

const AppOutlet = React.lazy(() => import('../slices/app-outlet'));
const SignIn = React.lazy(() => import('../slices/sign-in'));
const SignUp = React.lazy(() => import('../slices/sign-up'));
const MainLayout = React.lazy(() => import('../slices/main-layout'));
const Dashboard = React.lazy(() => import('../slices/dashboard'));
// const Provisions = React.lazy(() => import('../slices/provisions')); //unused atm, enable if need
const TermsConditionsPolicies = React.lazy(() => import('../slices/terms-conditions-policies'));
const SettingsProfile = React.lazy(() => import('../slices/settings-profile'));
const DocumentPackLayout = React.lazy(() => import('../slices/document-pack-layout/document-pack-layout'));
const Documents = React.lazy(() => import('../slices/documents/documents'));
const Packs = React.lazy(() => import('../slices/packs/packs'));
const StampSeries = React.lazy(() => import('../slices/stamp-series'));
const StampSeriesDetails = React.lazy(() => import('../slices/stamp-series-details'));
const WorkflowV3List = React.lazy(() => import('../slices/workflows-v3-list'));
const WorkflowV4List = React.lazy(() => import('../slices/workflows-v4-list'));
const WorkflowDetails = React.lazy(() => import('../slices/workflow-details'));
const WorkflowDetailsV4 = React.lazy(() => import('../slices/workflow-details-v4'));
const Contacts = React.lazy(() => import('../slices/contacts'));
const StampGroup = React.lazy(() => import('../slices/stamp-group'));
const ActivityLogs = React.lazy(() => import('../slices/activity-logs'));
const EbgNotifications = React.lazy(() => import('../slices/ebg-notifications'));
const Template = React.lazy(() => import('../slices/template'));
const TemplateV4List = React.lazy(() => import('../slices/templates-v4-list'));
const SettingsDocument = React.lazy(() => import('../slices/settings-document'));
const PdfEditor = React.lazy(() => import('../slices/pdf-editor'));
const PdfEditorV4 = React.lazy(() => import('../slices/pdf-editor/views/v4/pdf-editor-v4'));
const TextEditor = React.lazy(() => import('../slices/text-editor'));
// const PdfFieldsView = React.lazy(() => import('../slices/pdf-editor/views/pdf-fields-view'));
const DepartmentBranding = React.lazy(() => import('../slices/department-branding'));
const DepartmentSecurity = React.lazy(() => import('../slices/department-security'));
const SettingsEsignAndSeal = React.lazy(() => import('../slices/settings-esign-and-seal'));
const SettingsAPI = React.lazy(() => import('../slices/settings-api'));
const SettingsNotifications = React.lazy(() => import('../slices/settings-notifications'));
const DepartmentStamp = React.lazy(() => import('../slices/department-stamp'));
const NeslEsignConfig = React.lazy(() => import('../slices/department-nesl-config'));
const DepartmentInviteeConfigs = React.lazy(() => import('../slices/department-invitee-configs'));
const SettingsESignature = React.lazy(() => import('../slices/settings-esignature'));
const WalletPurchaseHistory = React.lazy(() => import('../slices/wallet-purchase-history'));
const SFTP = React.lazy(() => import('../slices/department-sftp'));
const DepartmentDocumentConfigs = React.lazy(() => import('../slices/department-document-configs'));
const PaymentCollect = React.lazy(() => import('../slices/department-payment-collect'));
const WorkflowBuilderConfig = React.lazy(() => import('../slices/department-workflow-builder-config'));
const IntegrationConfigs = React.lazy(() => import('../slices/department-integration-configs'));
const WalletConsumptionHistory = React.lazy(() => import('../slices/wallet-consumption-history'));
const WalletBillingProfiles = React.lazy(() => import('../slices/wallet-billing-profiles'));
const WalletInvoices = React.lazy(() => import('../slices/wallet-invoices'));
const WalletPurchase = React.lazy(() => import('../slices/wallet-purchase'));
const AdminDepartments = React.lazy(() => import('../slices/admin-departments'));
const AdminNewUsers = React.lazy(() => import('../slices/admin-new-users'));
const AdminUsers = React.lazy(() => import('../slices/admin-users'));
const WalletLowBalanceThreshold = React.lazy(() => import('../slices/wallet-low-balance-threshold'));
const DocumentDetailsGuard = React.lazy(() => import('../slices/document-details'));
const AuthenticatePage = React.lazy(() => import('../slices/authenticate'));
const SsoCallback = React.lazy(() => import('../slices/sso-callback'));
const CreateWorkflow = React.lazy(() => import('../slices/create-workflow'));
const EditV4DraftWorkflow = React.lazy(() => import('../slices/edit-v4-draft-workflow'));
const RunWorkflow = React.lazy(() => import('../slices/run-workflow'));
const CreateDocumentV4 = React.lazy(() => import('../slices/create-document-v4'));
const CustomFields = React.lazy(() => import('../slices/custom-fields'));
const Reports = React.lazy(() => import('../slices/reports'));
const Automation = React.lazy(() => import('../slices/automation'));
const BulkDscSigning = React.lazy(() => import('../slices/bulk-dsc-signing'));
const BulkSigningList = React.lazy(() => import('../slices/bulk-dsc-signing/views/bulk-signing-list/bulk-signing-list'));

const Route404 = () => {
  useEffect(() => {
    notification.error('MSG_PAGE_ACCESS_DENIED');
  });

  return <Navigate to={RL.dashboard} />;
};

const AppRoutes = [
  {
    element: <AppOutlet />,
    path: '/',
    children: [
      {
        index: true,
        element: <Navigate to={RL.dashboard} />,
      },
      {
        path: RL.authenticate,
        element: <AuthenticatePage />,
      },
      {
        path: `${RL.sso}/${RL.callback}`,
        element: <SsoCallback />,
      },
      {
        path: RL.signIn,
        element: <AntiProtectedRoute><SignIn /></AntiProtectedRoute>,
      },
      {
        path: RL.signUp,
        element: <AntiProtectedRoute><SignUp /></AntiProtectedRoute>,
      },
      {
        path: `${RL.dt}/${RL.v4}`,
        children: [
          {
            path: RL.edit,
            element: <ProtectedRoute><TextEditor /></ProtectedRoute>,
          },
          {
            path: `${RL.edit}/:templateId`,
            element: <ProtectedRoute><TextEditor /></ProtectedRoute>,
          },
        ],
      },
      {
        path: RL.pdfEditor,
        children: [
          {
            index: true,
            element: <ProtectedRoute><Navigate to={RL.addFields} /></ProtectedRoute>,
          },
          {
            path: `${RL.addFields}/${RL.colonId}`,
            element: <ProtectedRoute><PdfEditor tabId={PdfEditorTabs.ADD} /></ProtectedRoute>,
          },
          {
            path: `${RL.viewFields}/${RL.colonId}`,
            element: <ProtectedRoute><PdfEditor tabId={PdfEditorTabs.FIELDS} /></ProtectedRoute>,
          },
          {
            path: `${RL.previewPdf}/${RL.colonId}`,
            element: <ProtectedRoute><PdfEditor preview /></ProtectedRoute>,
          },
          {
            path: RL.v4,
            children: [
              {
                path: `${RL.addFields}/${RL.colonId}`,
                element: <ProtectedRoute><PdfEditorV4 tabId={PdfEditorTabs.ADD} /></ProtectedRoute>,
              },
              {
                path: `${RL.viewFields}/${RL.colonId}`,
                element: (
                  <ProtectedRoute>
                    <PdfEditorV4 tabId={PdfEditorTabs.FIELDS} />
                  </ProtectedRoute>
                ),
              },
              {
                path: `${RL.previewPdf}/${RL.colonId}`,
                element: <ProtectedRoute><PdfEditorV4 preview /></ProtectedRoute>,
              },
            ],
          },
        ],
      },
      {
        path: RL.document,
        children: [
          {
            index: true,
            element: <Navigate to={RL.details} />,
          },
          {
            path: RL.details,
            element: <DocumentDetailsGuard type={ForDocumentDetails.PRIVATE} />,
          },
        ],
      },
      {
        path: RL.document,
        children: [
          {
            path: RL.public,
            element: (
              <DocumentDetailsGuard type={ForDocumentDetails.PUBLIC} />
            ),
          },
        ],
      },
      {
        path: RL.bulkSigning,
        featureId: FeatureKeys.BULK_DSC_SIGNING,
        element: <ProtectedRoute><BulkDscSigning /></ProtectedRoute>,
        children: [
          {
            index: true,
            element: <Navigate to={RL.list} />,
          },
          {
            path: RL.list,
            element: <BulkSigningList />,
          },
          {
            path: RL.details,
            element: <DocumentDetailsGuard type={ForDocumentDetails.PRIVATE} />,
          },
        ],
      },
      {
        path: RL.termsConditions,
        element: <TermsConditionsPolicies type={TcpTypes.TERMS_CONDITIONS} />,
      },
      {
        path: `${RL.document}/${RL.v4}/${RL.create}`,
        element: <ProtectedRoute><CreateDocumentV4 /></ProtectedRoute>,
        featureId: FeatureKeys.NEW_DOCUMENT,
      },
      {
        path: `${RL.workflow}/${RL.run}`,
        element: <ProtectedRoute><RunWorkflow /></ProtectedRoute>,
      },
      {
        path: `${RL.workflow}/${RL.v4}/${RL.create}`,
        element: <ProtectedRoute><CreateWorkflow /></ProtectedRoute>,
      },
      {
        path: `${RL.workflow}/${RL.v4}/${RL.draft}/${RL.edit}`,
        element: <ProtectedRoute><EditV4DraftWorkflow /></ProtectedRoute>,
      },
      {
        element: <ProtectedRoute><MainLayout /></ProtectedRoute>,
        children: [
          {
            path: RL.dashboard,
            children: [
              {
                index: true,
                element: <Navigate to={RL.home} />,
              },
              {
                path: RL.home,
                element: <Dashboard />,
              },
              {
                path: RL.activity,
                element: <ActivityLogs />,
              },
              {
                path: RL.templates,
                children: [
                  {
                    index: true,
                    element: <Navigate to={RL.v3} />,
                  },
                  {
                    path: RL.v3,
                    element: <Template title="TEMPLATES" />,
                  },
                  {
                    path: RL.v4,
                    featureId: FeatureKeys.TEMPLATE_V4_MODULE,
                    children: [
                      {
                        index: true,
                        element: <Navigate to={RL.published} />,
                      },
                      {
                        path: RL.published,
                        element: <TemplateV4List tabId="published" />,
                      },
                      {
                        path: RL.drafts,
                        element: <TemplateV4List tabId="drafts" />,
                      },
                    ],
                  },
                ],
              },
              {
                path: RL.workflows,
                children: [
                  {
                    index: true,
                    element: <Navigate to={RL.v3} />,
                  },
                  {
                    path: RL.v4,
                    children: [
                      {
                        index: true,
                        element: <WorkflowV4List tabId={WorkflowsTabsIds.v4} />,
                      },
                      {
                        path: `${RL.colonId}/${RL.details}`,
                        element: <WorkflowDetailsV4 />,
                        featureId: FeatureKeys.WORKFLOW_V4_DETAILS,
                      },
                    ],
                  },
                  {
                    path: RL.v3,
                    element: <WorkflowV3List tabId={WorkflowsTabsIds.v3} />,
                  },
                  {
                    path: RL.colonId,
                    children: [
                      {
                        index: true,
                        element: <Navigate to={RL.details} />,
                      },
                      {
                        path: RL.details,
                        element: <WorkflowDetails />,
                      },
                    ],
                  },
                ],
                featureId: FeatureKeys.FULL_WORKFLOW_V4_MODULE,
              },
              {
                path: `${RL.workflows}/${RL.v4}`,
                featureId: FeatureKeys.ONLY_WORKFLOW_V4_MODULE,
                children: [
                  {
                    index: true,
                    element: <WorkflowV4List />,
                  },
                  {
                    path: `${RL.colonId}/${RL.details}`,
                    element: <WorkflowDetailsV4 />,
                    featureId: FeatureKeys.WORKFLOW_V4_DETAILS,
                  },
                ],
              },
              {
                path: `${RL.workflows}/${RL.v3}`,
                element: <WorkflowV3List />,
                featureId: FeatureKeys.ONLY_WORKFLOW_V3_MODULE,
              },
              {
                path: `${RL.workflows}/${RL.colonId}`,
                featureId: FeatureKeys.ONLY_WORKFLOW_V3_MODULE,
                children: [
                  {
                    index: true,
                    element: <Navigate to={RL.details} />,
                  },
                  {
                    path: RL.details,
                    element: <WorkflowDetails />,
                  },
                ],
              },
              {
                path: RL.customFields,
                element: <CustomFields />,
                featureId: FeatureKeys.CUSTOM_FIELDS,
                featureConfigId: FeatureConfigIds.CUSTOM_FIELDS,
              },
              {
                path: RL.contacts,
                element: <Contacts to={RL.contacts} />,
                featureId: FeatureKeys.CONTACT_BOOK,
              },
              {
                path: `${RL.reports}/*`,
                element: <Reports />,
                featureId: FeatureKeys.REPORTS_MODULE,
              },
              {
                path: RL.ebgNotifications,
                children: [
                  {
                    index: true,
                    element: <Navigate to={RL.pending} />,
                  },
                  {
                    path: RL.pending,
                    element: <EbgNotifications tabId={EbgNotificationsTabsIds.PENDING} />,
                  },
                  {
                    path: RL.reviewed,
                    element: <EbgNotifications tabId={EbgNotificationsTabsIds.REVIEWED} />,
                  },
                  {
                    path: RL.archive,
                    element: <EbgNotifications tabId={EbgNotificationsTabsIds.ARCHIVE} />,
                  },
                ],
              },
            ],
          },
          {
            path: RL.documents,
            children: [
              {
                index: true,
                element: <Navigate to={RL.sent} />,
              },
              {
                path: RL.all,
                element: <DocumentPackLayout type={DocumentTypes.ALL} />,
                children: [
                  { index: true, element: <Documents /> },
                ],
              },
              {
                path: RL.sent,
                element: <DocumentPackLayout type={DocumentTypes.SENT} />,
                children: [
                  { index: true, element: <Navigate to={RL.docs} /> },
                  { path: RL.docs, element: <Documents /> },
                  { path: RL.packs, element: <Packs />, featureId: FeatureKeys.WORKFLOW_V4_MODULE },
                ],
              },
              {
                path: RL.received,
                element: <DocumentPackLayout type={DocumentTypes.RECEIVED} />,
                children: [
                  { index: true, element: <Documents /> },
                ],
              },
              {
                path: RL.processing,
                element: <DocumentPackLayout type={DocumentTypes.PROCESSING} />,
                children: [
                  { index: true, element: <Documents /> },
                ],
              },
              {
                path: RL.cc,
                element: <DocumentPackLayout type={DocumentTypes.CC} />,
                children: [
                  { index: true, element: <Documents /> },
                ],
              },
              {
                path: RL.drafts,
                element: <DocumentPackLayout type={DocumentTypes.DRAFTS} />,
                children: [
                  { index: true, element: <Navigate to={RL.docs} /> },
                  { path: RL.docs, element: <Documents /> },
                  { path: RL.packs, element: <Packs />, featureId: FeatureKeys.WORKFLOW_V4_MODULE },
                ],
              },
              {
                path: RL.folder,
                element: <DocumentPackLayout type={DocumentTypes.FOLDER} />,
                children: [
                  { index: true, element: <Navigate to={RL.docs} /> },
                  { path: RL.docs, element: <Documents /> },
                  { path: RL.packs, element: <Packs />, featureId: FeatureKeys.WORKFLOW_V4_MODULE },
                ],
              },
              {
                path: RL.actionRequired,
                element: <DocumentPackLayout type={DocumentTypes.ACTION_REQUIRED} />,
                children: [
                  { index: true, element: <Documents /> },
                ],
              },
              {
                path: RL.waitingForOthers,
                element: <DocumentPackLayout type={DocumentTypes.WAITING_FOR_OTHERS} />,
                children: [
                  { index: true, element: <Documents /> },
                ],
              },
              {
                path: RL.expiringSoon,
                element: <DocumentPackLayout type={DocumentTypes.EXPIRING_SOON} />,
                children: [
                  { index: true, element: <Documents /> },
                ],
              },
              {
                path: RL.failed,
                element: <DocumentPackLayout type={DocumentTypes.FAILED} />,
                children: [
                  { index: true, element: <Documents /> },
                ],
              },
              {
                path: RL.neslFailureCases,
                element: <DocumentPackLayout type={DocumentTypes.NESL_ERRORS} />,
                children: [
                  { index: true, element: <Documents /> },
                ],
              },
              {
                path: RL.expired,
                element: <DocumentPackLayout type={DocumentTypes.EXPIRED} />,
                children: [
                  { index: true, element: <Documents /> },
                ],
              },
              {
                path: RL.completed,
                element: <DocumentPackLayout type={DocumentTypes.COMPLETED} />,
                children: [
                  { index: true, element: <Navigate to={RL.docs} /> },
                  { path: RL.docs, element: <Documents /> },
                  { path: RL.packs, element: <Packs />, featureId: FeatureKeys.WORKFLOW_V4_MODULE },
                ],
              },
              {
                path: RL.rejected,
                element: <DocumentPackLayout type={DocumentTypes.REJECTED} />,
                children: [
                  { index: true, element: <Documents /> },
                ],
              },
            ],
          },
          {
            path: RL.stamps,
            restrictImpersonators: true,
            featureId: FeatureKeys.STAMP_MODULE,
            featureConfigId: FeatureConfigIds.STAMP_MODULE,
            children: [
              {
                index: true,
                element: <Navigate to={`${RL.series}/${RL.all}`} />,
              },
              {
                path: RL.series,
                children: [
                  {
                    index: true,
                    element: <Navigate to={RL.all} />,
                  },
                  {
                    path: RL.all,
                    element: <StampSeries tabId={SeriesTabsIds.ALL} />,
                  },
                  {
                    path: RL.favourites,
                    element: <StampSeries tabId={SeriesTabsIds.FAVOURITES} />,
                  },
                  {
                    path: RL.drafts,
                    element: <StampSeries tabId={SeriesTabsIds.DRAFTS} />,
                  },
                  {
                    path: RL.colonId,
                    children: [
                      {
                        index: true,
                        element: <Navigate to={RL.details} />,
                      },
                      {
                        path: RL.details,
                        element: <StampSeriesDetails tabId={SeriesDetailsTabsIds.DETAILS} />,
                      },
                      {
                        path: RL.stamps,
                        element: <StampSeriesDetails tabId={SeriesDetailsTabsIds.STAMPS} />,
                      },
                    ],
                  },
                ],
              },
              {
                path: RL.groups,
                featureId: FeatureKeys.STAMP_GROUP_MODULE,
                featureConfigId: FeatureConfigIds.STAMP_GROUP,
                children: [
                  {
                    index: true,
                    element: <Navigate to={RL.all} />,
                  },
                  {
                    path: RL.all,
                    element: <StampGroup tabId={SeriesTabsIds.ALL} />,
                  },
                  {
                    path: RL.favourites,
                    element: <StampGroup tabId={SeriesTabsIds.FAVOURITES} />,
                  },
                ],
              },
            ],
          },
          {
            path: RL.accounts,
            children: [
              {
                index: true,
                element: <Navigate to={`${RL.settings}/${RL.profile}`} />,
              },
              {
                // Served by @leegality/sequence-automation, mounted as a peer of
                // settings rather than inside it: the package owns six sub-features
                // and multiLevelMenuItems only nests two levels deep, so it needs to
                // sit at section level to render them as children.
                // The splat is required, the package renders its own <Routes>.
                path: `${RL.automation}/*`,
                element: <Automation />,
                featureId: FeatureKeys.AUTOMATION_MODULE,
                isMobileUnsupported: true,
              },
              {
                path: RL.settings,
                children: [
                  {
                    path: RL.profile,
                    element: <SettingsProfile />,
                  },
                  {
                    path: RL.notifications,
                    featureId: FeatureKeys.NOTIFICATIONS_MODULE,
                    children: [
                      {
                        index: true,
                        element: <SettingsNotifications />,
                      },
                      {
                        path: RL.colonId,
                        element: <SettingsNotifications />,
                      },
                    ],
                  },
                  {
                    path: RL.documents,
                    element: <SettingsDocument />,
                    isMobileUnsupported: true,
                  },
                  {
                    path: RL.esignAndSeal,
                    element: <SettingsEsignAndSeal />,
                    isMobileUnsupported: true,
                  },
                  {
                    path: RL.api,
                    element: <SettingsAPI />,
                    featureId: FeatureKeys.API_MODULE,
                  },
                ],
              },
              {
                path: RL.department,
                featureId: FeatureKeys.DEPARTMENT_MODULE,
                children: [
                  {
                    path: RL.branding,
                    element: <DepartmentBranding />,
                  },
                  {
                    path: RL.stamp,
                    element: <DepartmentStamp />,
                    isMobileUnsupported: true,
                    featureId: FeatureKeys.STAMP_MODULE,
                  },
                  {
                    path: RL.inviteeConfigs,
                    element: <DepartmentInviteeConfigs />,
                    isMobileUnsupported: true,
                  },
                  {
                    path: RL.documentConfigs,
                    element: <DepartmentDocumentConfigs />,
                    isMobileUnsupported: true,
                  },
                  {
                    path: RL.eSignature,
                    element: <SettingsESignature />,
                    isMobileUnsupported: true,
                  },
                  {
                    path: RL.security,
                    element: <DepartmentSecurity />,
                    isMobileUnsupported: true,
                  },
                  {
                    path: RL.neslEsignConfig,
                    featureId: FeatureKeys.NESL_MODULE,
                    children: [
                      {
                        index: true,
                        element: <Navigate to={RL.neslProfiles} />,
                      },
                      {
                        path: RL.neslProfiles,
                        element: (
                          <NeslEsignConfig
                            tabId={DepartmentNeslConfigsTabsIds.NESL_PROFILES}
                          />
                        ),
                        isMobileUnsupported: true,
                      },
                      {
                        path: RL.eventReviewers,
                        featureId: FeatureKeys.EBG_NOTIFICATION,
                        element: (
                          <NeslEsignConfig
                            tabId={DepartmentNeslConfigsTabsIds.EVENT_REVIEWERS}
                          />
                        ),
                        isMobileUnsupported: true,
                      },
                    ],
                  },
                  {
                    path: RL.sftp,
                    element: <SFTP />,
                    featureId: FeatureKeys.SFTP_MODULE,
                    isMobileUnsupported: true,
                  },
                  {
                    path: RL.paymentCollect,
                    element: <PaymentCollect />,
                    featureId: FeatureKeys.PAYMENT_COLLECTION,
                    isMobileUnsupported: true,
                  },
                  {
                    path: RL.workflowBuilderConfig,
                    element: <WorkflowBuilderConfig />,
                    isMobileUnsupported: true,
                    featureId: FeatureKeys.WORKFLOW_BUILDER_CONFIG,
                  },
                  {
                    featureId: FeatureKeys.SENDING_SDK,
                    path: RL.integrationConfigs,
                    element: <IntegrationConfigs />,
                    isMobileUnsupported: true,
                  },
                ],
              },
              {
                path: RL.wallet,
                featureId: FeatureKeys.WALLET_MODULE,
                children: [
                  {
                    path: RL.purchaseHistory,
                    children: [
                      {
                        index: true,
                        element: <Navigate to={RL.esign} />,
                      },
                      {
                        path: RL.esign,
                        element: <WalletPurchaseHistory tabId={PurchaseHistoryTabsIds.ESIGN} />,
                        isMobileUnsupported: true,
                      },
                      {
                        path: RL.stamp,
                        element: <WalletPurchaseHistory tabId={PurchaseHistoryTabsIds.STAMP} />,
                        isMobileUnsupported: true,
                      },
                    ],
                  },
                  {
                    path: RL.consumptionHistory,
                    children: [
                      {
                        index: true,
                        element: <Navigate to={RL.esign} />,
                      },
                      {
                        path: RL.esign,
                        isMobileUnsupported: true,
                        element: (
                          <WalletConsumptionHistory
                            tabId={ConsumptionHistoryTabsIds.ESIGN}
                          />
                        ),
                      },
                      {
                        path: RL.stamp,
                        isMobileUnsupported: true,
                        element: (
                          <WalletConsumptionHistory
                            tabId={ConsumptionHistoryTabsIds.STAMP}
                          />
                        ),
                      },
                    ],
                  },
                  {
                    path: RL.billingProfiles,
                    element: <WalletBillingProfiles />,
                    isMobileUnsupported: true,
                  },
                  {
                    path: RL.invoices,
                    children: [
                      {
                        index: true,
                        element: <Navigate to={RL.taxInvoices} />,
                      },
                      {
                        path: RL.taxInvoices,
                        isMobileUnsupported: true,
                        element: (
                          <WalletInvoices
                            tabId={InvoicesTabsIds.TAX_INVOICES}
                          />
                        ),
                      },
                      {
                        path: RL.proformaInvoices,
                        element: (
                          <WalletInvoices
                            tabId={InvoicesTabsIds.PROFORMA_INVOICES}
                          />
                        ),
                      },
                    ],
                  },
                  {
                    path: RL.purchase,
                    element: <WalletPurchase />,
                  },
                  {
                    path: RL.lowBalanceConfigs,
                    isMobileUnsupported: true,
                    element: <WalletLowBalanceThreshold />,
                  },
                ],
              },
              {
                path: RL.admin,
                featureId: FeatureKeys.GENERIC_ADMIN_ROLE,
                children: [
                  {
                    path: RL.departments,
                    element: <AdminDepartments />,
                    isMobileUnsupported: true,
                  },
                  {
                    path: RL.users,
                    element: <AdminUsers />,
                    isMobileUnsupported: true,
                  },
                  {
                    path: RL.newUsers,
                    element: <AdminNewUsers />,
                    isMobileUnsupported: true,
                  },
                ],
              },
            ],
          },
          {
            path: RL.wild,
            element: <Route404 />,
          },
        ],
      },
    ],
  },
];

export default AppRoutes;
